import { useEffect, useState } from 'react';
import { Sparkles, TrendingUp, Info } from 'lucide-react';
import { usePipelineStore, calcQuarterlyBonus, effectiveMrr } from '@/stores/pipeline.store';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Probability-weighted potential bonus if all pipeline deals close at their stage probability
function calcPipelinePotential(deals: ReturnType<typeof usePipelineStore.getState>['deals'], overrides: Map<string, string>): number {
  const weightedMrr = deals.reduce((sum, d) => {
    const stage = overrides.get(d.id) ?? d.stage;
    const amount = d.amountCad ?? d.amount ?? 0;
    const stageWeights: Record<string, number> = {
      lead: 0.10, prospect: 0.20, negotiating: 0.40,
      verbal: 0.70, signed: 1, active: 1, churned: 0,
    };
    return sum + amount * (stageWeights[stage] ?? 0);
  }, 0);
  return calcQuarterlyBonus(weightedMrr);
}

export function BonusBar() {
  const { deals, optimisticMoves, lastBonusDelta, clearBonusDelta } = usePipelineStore();
  const [showDelta, setShowDelta] = useState(false);
  const [deltaVal, setDeltaVal] = useState(0);

  // Signed/active MRR → the real projected bonus
  const signedMrr = deals.reduce((sum, d) => {
    const stage = optimisticMoves.get(d.id) ?? d.stage;
    return sum + effectiveMrr(d, stage);
  }, 0);
  const projectedBonus = calcQuarterlyBonus(signedMrr);

  // Weighted pipeline potential bonus
  const potentialBonus = calcPipelinePotential(deals, optimisticMoves);
  const uplift = Math.max(0, potentialBonus - projectedBonus);

  useEffect(() => {
    if (lastBonusDelta && lastBonusDelta !== 0) {
      setDeltaVal(lastBonusDelta);
      setShowDelta(true);
      const t = setTimeout(() => {
        setShowDelta(false);
        clearBonusDelta();
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [lastBonusDelta, clearBonusDelta]);

  return (
    <div className="rounded-xl border bg-gradient-to-r from-blue-950 to-blue-900 p-5 text-white shadow-lg">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

        {/* Left — bonus numbers */}
        <div className="flex items-center gap-6">
          <div>
            <p className="text-xs font-medium uppercase tracking-widest text-blue-300">Projected Q2 Bonus</p>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-extrabold tabular-nums">
                {formatCurrency(projectedBonus, 'CAD')}
              </p>
              {showDelta && (
                <span
                  className={cn(
                    'animate-in fade-in slide-in-from-bottom-2 text-sm font-bold',
                    deltaVal > 0 ? 'text-green-400' : 'text-red-400',
                  )}
                >
                  {deltaVal > 0 ? '+' : ''}{formatCurrency(deltaVal, 'CAD')}
                </span>
              )}
            </div>
            <p className="text-xs text-blue-400">based on signed deals</p>
          </div>

          {uplift > 0 && (
            <>
              <div className="hidden h-10 w-px bg-blue-700 sm:block" />
              <div className="hidden sm:block">
                <div className="flex items-center gap-1">
                  <p className="text-xs font-medium uppercase tracking-widest text-blue-300">Pipeline Potential</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-blue-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[220px] text-xs leading-snug">
                        Each deal is weighted by its stage probability (Lead 10%, Prospect 20%, Negotiating 40%, Verbal 70%, Signed 100%), then run through the commission tiers. This is what you'd earn if all open deals closed at their current stage probability.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-xl font-bold tabular-nums text-emerald-400">
                  +{formatCurrency(uplift, 'CAD')}
                </p>
                <p className="text-xs text-blue-400">probability-weighted</p>
              </div>
            </>
          )}

          <div className="hidden h-10 w-px bg-blue-700 sm:block" />

          <div className="hidden sm:block">
            <p className="text-xs font-medium uppercase tracking-widest text-blue-300">Signed MRR</p>
            <p className="text-xl font-bold tabular-nums">{formatCurrency(signedMrr, 'CAD')}</p>
            <p className="text-xs text-blue-400">this quarter</p>
          </div>
        </div>

        {/* Right — uplift nudge or VP progress */}
        <div className="min-w-[220px]">
          {uplift > 0 ? (
            <div className="rounded-lg bg-blue-800/60 px-4 py-3 text-sm">
              <div className="flex items-center gap-2 font-semibold text-emerald-300">
                <Sparkles className="h-4 w-4" />
                <span>Close more to unlock</span>
              </div>
              <p className="mt-1 text-blue-200 text-xs leading-snug">
                Move deals to Signed to turn{' '}
                <span className="font-semibold text-emerald-300">{formatCurrency(uplift, 'CAD')}</span>{' '}
                of pipeline into your bonus.
              </p>
            </div>
          ) : (
            <div className="rounded-lg bg-blue-800/60 px-4 py-3 text-sm">
              <div className="flex items-center gap-2 font-semibold text-yellow-300">
                <TrendingUp className="h-4 w-4" />
                <span>Pipeline is empty</span>
              </div>
              <p className="mt-1 text-blue-200 text-xs leading-snug">
                Add deals to your pipeline to start tracking your bonus potential.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
